import { useEffect } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import GrainOverlay from "../components/GrainOverlay";

const Section = ({ title, children, testId }) => (
  <div className="mt-14 first:mt-0" data-testid={testId}>
    <h2 className="h-display text-2xl md:text-3xl mb-4 text-ink">{title}</h2>
    <div className="space-y-4 text-base md:text-lg leading-relaxed text-ink/80">
      {children}
    </div>
  </div>
);

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy | GoneSoon";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-page" className="relative">
      <GrainOverlay />
      <div className="bg-cream">
        <Nav variant="cream" />
      </div>

      <section className="bg-cream text-ink">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
          <div className="pill text-ink/70 border-ink/20 mb-8">
            <span className="pill-dot" />
            Legal
          </div>
          <h1 data-testid="privacy-headline" className="h-display text-[2.5rem] md:text-[4rem] leading-[0.95] max-w-[16ch]">
            Privacy Policy
          </h1>
          <p className="mt-6 text-ink/60 text-sm uppercase tracking-[0.18em]">
            Effective August 3, 2026
          </p>
        </div>

        <div className="max-w-[900px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
          <Section title="Who we are" testId="privacy-who-we-are">
            <p>
              GoneSoon ("GoneSoon", "we", "us", or "our") operates a membership-based platform
              connecting people with limited-time offers ("drops") from local merchants, available
              through our mobile apps (iOS and Android), our website at gonesoon.in, and WhatsApp.
            </p>
            <p>
              This policy explains what information we collect, how we use it, and the choices you
              have — across the mobile app, the website, and any interaction with our WhatsApp
              Business number.
            </p>
          </Section>

          <Section title="Information we collect" testId="privacy-info-we-collect">
            <p><strong>Account information.</strong> Your first name, mobile number, and membership/subscription status. We use your mobile number to send a one-time verification code (OTP) to sign you in.</p>
            <p><strong>WhatsApp messages.</strong> If you (or a merchant) message our WhatsApp Business number, we receive your WhatsApp number and the content of the messages you send us, through Meta's WhatsApp Business Platform. See "WhatsApp Business messaging" below for detail.</p>
            <p><strong>Claim and redemption history.</strong> Which drops you've claimed, your membership/trial status, and redemption records (including one-time QR passes).</p>
            <p><strong>Camera access (merchant staff only).</strong> Merchant staff accounts request camera access solely to scan a customer's redemption QR code at the point of sale. We do not access the camera at any other time, and we do not store photos or video.</p>
            <p><strong>What we do not collect.</strong> We do not request or track your GPS location. We do not use advertising trackers or third-party analytics SDKs. We do not sell your information.</p>
          </Section>

          <Section title="How we use your information" testId="privacy-how-we-use">
            <p>We use the information above to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Verify your identity and sign you into your account (OTP via WhatsApp)</li>
              <li>Let you claim and redeem drops, and manage your membership/subscription</li>
              <li>Onboard and qualify prospective merchants who message us on WhatsApp</li>
              <li>Provide customer support and respond to messages you send us</li>
              <li>Maintain the security, integrity, and reliability of our services</li>
              <li>Comply with legal obligations where applicable</li>
            </ul>
          </Section>

          <Section title="WhatsApp Business messaging" testId="privacy-whatsapp">
            <p>
              We use the WhatsApp Business Platform, provided by Meta, to send one-time
              verification codes and to run conversational merchant onboarding (asking about
              business category and location before handing a qualified lead to our team).
            </p>
            <p>
              Any data we receive through this integration — your WhatsApp number, message
              content, and related metadata — is "Platform Data" made available to us by Meta.
              We only use it for the purposes described in this policy: account verification,
              merchant onboarding, and responding to the conversation you started with us. We do
              not use it for advertising, and we do not sell it or share it with data brokers.
            </p>
            <p>
              Meta processes this data as the operator of the WhatsApp Business Platform under
              its own terms; see Meta's Privacy Policy for how Meta handles data on its platform.
            </p>
          </Section>

          <Section title="Who we share information with" testId="privacy-sharing">
            <p>
              We do not sell your personal information. We do not use third-party analytics,
              advertising, or tracking services. The only outside party involved in delivering
              our service is Meta, as the provider of the WhatsApp Business Platform described
              above. Merchant staff can see a customer's first name only at the moment of QR
              redemption, to confirm identity.
            </p>
            <p>
              We may disclose information if required to do so by law, or in response to a valid
              legal request from a public authority.
            </p>
          </Section>

          <Section title="Where your data is stored" testId="privacy-storage">
            <p>
              Your data is stored in a database we operate and control directly. We do not use
              third-party data processors to store or process your personal information.
            </p>
          </Section>

          <Section title="Data retention and deletion" testId="privacy-retention">
            <p>
              We retain your information for as long as your account is active, or as needed to
              provide the service and meet legal obligations. You can request deletion of your
              account and associated personal data at any time from your Profile in the app, or
              by contacting us using the details below.
            </p>
          </Section>

          <Section title="Children's privacy" testId="privacy-children">
            <p>
              GoneSoon is not directed at children, and we do not knowingly collect information
              from anyone under the age of 18.
            </p>
          </Section>

          <Section title="Your rights" testId="privacy-your-rights">
            <p>
              You can ask us to access, correct, or delete your personal information at any time.
              To make a request, contact us using the details below.
            </p>
          </Section>

          <Section title="Changes to this policy" testId="privacy-changes">
            <p>
              We may update this policy from time to time. If we make material changes, we'll
              update the effective date above and, where appropriate, notify you directly.
            </p>
          </Section>

          <Section title="Contact us" testId="privacy-contact">
            <p>
              Questions about this policy or your data? Email us at{" "}
              <a href="mailto:privacy@gonesoon.in" className="ed-link text-ink">privacy@gonesoon.in</a>.
            </p>
          </Section>

          <div className="mt-16 pt-10 border-t border-ink/15">
            <Link to="/terms" data-testid="privacy-link-terms" className="ed-link text-ink">
              Read our Terms of Service →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
